import DatePicker from "react-datepicker";
import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';

import Loader from './Loader';
import BroadcastImages from './BroadcastImages';
import BroadcastAudio from './BroadcastAudio';
import BroadcastNotes from './BroadcastNotes';

import firebase from '../firebase';

import { MODALS } from '../utils/constants';

const FIELDS = {
    broadcastDate: 'broadcastDate',
    description: 'description',
    location: 'location',
    title: 'title',
    tracklist: 'tracklist',
};

function safeValueOf(dateOrNull) {
    return dateOrNull && dateOrNull.valueOf();
}

function BroadcastDetails({ broadcastId, getBroadcastDetails, isAdmin, setModalOpen, selectedBroadcast, setSelectedImage, setSelectedAudio }) {
    const [loading, setLoading] = useState(false);

    const [broadcastDate, setBroadcastDate] = useState(null);
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [title, setTitle] = useState('');
    const [tracklist, setTracklist] = useState('');

    const [fieldsEditing, setFieldsEditing] = useState([]);
    const [fieldSubmitting, setFieldSubmitting] = useState(null);

    useEffect(() => {
        getDetails();
    }, [broadcastId]);

    useEffect(() => {
        if (!broadcastDate) return;

        submitBroadcastDate(broadcastDate);
    }, [broadcastDate]);

    useEffect(() => {
        if (!selectedBroadcast) return;

        if (!fieldsEditing.includes(FIELDS.broadcastDate)) setBroadcastDate(selectedBroadcast.broadcastDate);
        if (!fieldsEditing.includes(FIELDS.description)) setDescription(selectedBroadcast.description);
        if (!fieldsEditing.includes(FIELDS.location)) setLocation(selectedBroadcast.location);
        if (!fieldsEditing.includes(FIELDS.title)) setTitle(selectedBroadcast.title);
        if (!fieldsEditing.includes(FIELDS.tracklist)) setTracklist(selectedBroadcast.tracklist);
    }, [selectedBroadcast]);

    async function getDetails() {
        setLoading(true);
        await getBroadcastDetails(broadcastId);
        setTimeout(() => setLoading(false), 100);
    }

    function handleChange(e) {
        const { value, name } = e.target;
        const currentValue = selectedBroadcast[name];

        if (value === currentValue) {
            const fields = fieldsEditing.filter(fieldName => fieldName !== name);
            setFieldsEditing(fields);
        } else {
            setFieldsEditing([...fieldsEditing, name]);
        }

        switch(name) {
            case FIELDS.location:
                setLocation(value);
                break;
            case FIELDS.title:
                setTitle(value);
                break;
            case FIELDS.tracklist:
                setTracklist(value);
                break;
            case FIELDS.description:
                setDescription(value);
                break;
        }
    }

    function constructBroadcastUpdate(fieldName) {
        switch(fieldName) {
            case FIELDS.location:
                return { [FIELDS.location]: location };
            case FIELDS.title:
                return { [FIELDS.title]: title };
            case FIELDS.tracklist:
                return { [FIELDS.tracklist]: tracklist };
            case FIELDS.description:
                return { [FIELDS.description]: description };
            default:
                return {};
        }
    }

    function submitBroadcastDate(updatedDate) {
        if (!selectedBroadcast) return;

        if (safeValueOf(updatedDate) === safeValueOf(selectedBroadcast.broadcastDate)) return;

        const updateFields = { [FIELDS.broadcastDate]: updatedDate ? Timestamp.fromDate(updatedDate) : null };
        return firebase.updateBroadcast(broadcastId, updateFields).then(() => {
            return getBroadcastDetails(broadcastId);
        });
    }

    async function handleFieldSubmit(e) {
        e.preventDefault();
        const fieldName = FIELDS[e.target.name];
        if (!fieldName) return;

        setFieldSubmitting(fieldName);
        const updateFields = constructBroadcastUpdate(fieldName);
        await firebase.updateBroadcast(broadcastId, updateFields);
        await getBroadcastDetails(broadcastId);
        setTimeout(() => {
            setFieldsEditing(fieldsEditing.filter(
                fieldEditing => fieldEditing !== fieldName
            ));
            setFieldSubmitting(null);
        }, 800);
    }

    if (loading) return (
        <div style={{ padding: '48px 0' }}>
            <Loader />
        </div>
    );

    if (!selectedBroadcast) return (
        <div>
            No broadcast found
        </div>
    );

    return (
        <div>
            <div className="card">
                <div className="card__account-header">
                    <h2 className="card__title">Broadcast details</h2>
                    <div>
                        {isAdmin &&
                            <div className="card__button-container">
                                <button
                                    className="mdl-button mdl-button--raised button-blue"
                                    onClick={() => setModalOpen(MODALS.broadcastAccessLink)}
                                    style={{ minWidth: '150px', float: 'right', marginBottom: '12px' }}>
                                    Add user
                                </button>
                            </div>
                        }
                    </div>
                </div>
                <div className="card__results">
                    <div className="card__item-group">
                        <form onSubmit={handleFieldSubmit} name={FIELDS.title}>
                            <label className="mdl-typography--body-1 text-bold" htmlFor={FIELDS.title}>
                                Title:
                            </label>
                            <div className="card__inline-edit">
                                <div>
                                    <input
                                        id={FIELDS.title}
                                        name={FIELDS.title}
                                        className="mdl-textfield__input"
                                        onChange={handleChange}
                                        disabled={fieldSubmitting === FIELDS.title}
                                        value={title} />
                                </div>
                                {(fieldsEditing.includes(FIELDS.title) || fieldSubmitting === FIELDS.title) && (
                                    <button disabled={fieldSubmitting === FIELDS.title}
                                        className="mdl-button mdl-button--raised button-blue">
                                        {fieldSubmitting === FIELDS.title ? "Saving..." : "Save" }
                                    </button>
                                )}
                            </div>
                        </form>
                        <label className="mdl-typography--body-1 text-bold" htmlFor={FIELDS.broadcastDate}>
                            Broadcast Date:
                        </label>
                        <div style={{ marginBottom: '12px' }}>
                            <DatePicker className="mdl-textfield__input" dateFormat="yyyy/MM/dd" selected={broadcastDate} onChange={setBroadcastDate} />
                        </div>
                        <form onSubmit={handleFieldSubmit} name={FIELDS.location}>
                            <label className="mdl-typography--body-1 text-bold" htmlFor={FIELDS.location}>
                                Your location:
                            </label>
                            <div className="card__inline-edit">
                                <div>
                                    <input
                                        id={FIELDS.location}
                                        name={FIELDS.location}
                                        className="mdl-textfield__input"
                                        onChange={handleChange}
                                        placeholder="City or Town"
                                        disabled={fieldSubmitting === FIELDS.location}
                                        value={location} />
                                </div>
                                {(fieldsEditing.includes(FIELDS.location) || fieldSubmitting === FIELDS.location) && (
                                    <button disabled={fieldSubmitting === FIELDS.location}
                                        className="mdl-button mdl-button--raised button-blue">
                                        {fieldSubmitting === FIELDS.location ? "Saving..." : "Save" }
                                    </button>
                                )}
                            </div>
                        </form>
                        <form onSubmit={handleFieldSubmit} name={FIELDS.description}>
                            <label className="mdl-typography--body-1 text-bold" htmlFor={FIELDS.description}>
                                Description:
                            </label>
                            <div className="card__inline-guide">
                                Tell us about the broadcast - include details about artists, themes, moods, genres or anything listeners should know. NTS will use this to write the public description.
                            </div>
                            <div className="card__inline-edit">
                                <textarea onChange={handleChange} value={description} name={FIELDS.description} id={FIELDS.description}
                                    maxLength="500"
                                    placeholder="Max 500 characters."
                                    disabled={fieldSubmitting === FIELDS.description}
                                    rows="9" cols="60"
                                />
                                {(fieldsEditing.includes(FIELDS.description) || fieldSubmitting === FIELDS.description) && (
                                    <button disabled={fieldSubmitting === FIELDS.description}
                                        className="mdl-button mdl-button--raised button-blue">
                                        {fieldSubmitting === FIELDS.description ? "Saving..." : "Save" }
                                    </button>
                                )}
                            </div>
                        </form>
                        {isAdmin && (
                            <div>
                                <div className="card__item-group-key text-bold">Linked users:</div>
                                <span>
                                    <ul
                                        style={{ display: 'inline-flex' }}
                                        className="card__user-email-list card__array">
                                        {selectedBroadcast.userEmails.map(userEmail => {
                                            return (
                                                <li key={userEmail}>
                                                    {userEmail}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="card">
                <BroadcastAudio
                    setModalOpen={setModalOpen}
                    audios={selectedBroadcast.audios}
                    setSelectedAudio={setSelectedAudio} />
            </div>
            <div className="card">
                <div className="card__account-header">
                    <h2 className="card__title">Tracklist</h2>
                </div>
                <form onSubmit={handleFieldSubmit} name={FIELDS.tracklist}>
                    <label className="mdl-typography--body-1 text-bold" htmlFor={FIELDS.tracklist} style={{ display: "none" }}>Tracklist</label>
                    <div className="card__inline-guide">
                        <strong>Important:</strong> Shows without full tracklists will <strong>no longer be archived</strong> on the site. They are required to ensure NTS can pay registered artists their royalties, and to help listeners discover artists.
                    </div>
                    <div className="card__inline-edit">
                        <textarea onChange={handleChange} value={tracklist} name={FIELDS.tracklist} id={FIELDS.tracklist}
                        placeholder="Use the following format and do not use a numbered list or bullet points:&#13;&#10;Artist Name - Track Title&#13;&#10;Artist Name - Track Title"
                        disabled={fieldSubmitting === FIELDS.tracklist}
                        rows="12" cols="60" />
                        {(fieldsEditing.includes(FIELDS.tracklist) || fieldSubmitting === FIELDS.tracklist) && (
                            <button disabled={fieldSubmitting === FIELDS.tracklist}
                                className="mdl-button mdl-button--raised button-blue">
                                {fieldSubmitting === FIELDS.tracklist ? "Saving..." : "Save" }
                            </button>
                        )}
                    </div>
                </form>
            </div>
            <BroadcastImages
                images={selectedBroadcast.images}
                setSelectedImage={setSelectedImage}
                setModalOpen={setModalOpen} />

            <BroadcastNotes
                notes={selectedBroadcast.notes}
                setModalOpen={setModalOpen} />

            <div className="card">
                <div className="card__account-header mdl-grid" style={{ padding: "0px"}}>
                    <div style={{ alignSelf: "center", marginRight: "20px" }} className="mdl-cell mdl-cell--8-col" >
                        When you have finished filling out the form, let the NTS team know to review.
                    </div>
                    <div>
                        <button
                            className="mdl-button mdl-button--raised button-blue mdl-cell mdl-cell--4-col"
                            onClick={() => setModalOpen(MODALS.broadcastNotify)}
                            style={{ minWidth: '200px' }}>
                            Submit For Review
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BroadcastDetails;
